import { WOODLAND_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/woodlandBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { breathingGlowBlobs, gustingWeather, multiPlaneParallax, biomeCloser } from '../presetBuilders';

/** Whispering Bamboo — a serene zen bamboo grove: soft green-gold filtered light streaming
 *  through a high canopy, drifting leaf motes, calm vertical bamboo-stalk silhouettes (LIT
 *  green columns, ordered + airy — NOT the dense dark of jungle), gentle ground mist, and a
 *  warm green-gold grade. Calm, meditative, minimal — the AIRY ZEN counterpoint: light + open,
 *  slender vertical bamboo that sways (whispers) on the breeze. Contrast kept (no pale wash). */
export const BAMBOO: AtmospherePreset = {
  id: 'bamboo',
  layers: [
    // Soft filtered-grove sky — gentle sage zenith → warm green-gold horizon (the light that
    // streams down through the canopy). Kept saturated, not pale, so the bamboo columns read.
    { kind: 'skyGradient', from: 0x9ecb80, to: 0xe4ecae },
    // High filtered sun + broad soft halo — the calm light source the god-rays + bloom read from.
    { kind: 'celestial', atX: 0.68, atY: 0.14, color: P.sunHaze, haloColor: 0xfdf0c0, size: 88, haloScale: 3.0, coreAlpha: 0.78, haloAlpha: 0.18 },
    // Drifting leaf motes adrift in the still air (fresh green + warm gold, slow wander).
    { kind: 'fireflies', count: 20, colors: [P.leaf, P.firefly, 0xc6e495], sizeMin: 4, sizeMax: 11, driftPx: 40, periodMinMs: 6000, periodMaxMs: 13000, alphaMin: 0.16, alphaMax: 0.55 },
    // Green-gold light shafts filtering down between the stalks — many, soft, near-vertical, calm.
    { kind: 'godRays', count: 5, color: 0xeaf0b8, widthMin: 70, widthMax: 160, height: 540, tiltDeg: 6, periodMinMs: 8000, periodMaxMs: 15000, alphaMin: 0.08, alphaMax: 0.26 },
    // Canopy sun bloom (top) + soft green grove radiance (bottom) — swelling slowly so it breathes.
    breathingGlowBlobs(
      [
        { color: P.sunHaze, atX: 0.68, atY: 0.13, size: 440, alpha: 0.08 },
        { color: P.leaf, atX: 0.5, atY: 0.97, size: 600, alpha: 0.07 },
      ],
      { periodMs: 7000, min: 0.85, max: 1.2 },
    ),
    // Soft green-gold light bed shimmering low through the grove.
    { kind: 'lightField', count: 12, palette: [P.sunHaze, P.leaf, 0xbfe096], sizeMin: 120, sizeMax: 300, alphaMin: 0.04, alphaMax: 0.13, driftRadius: 58, periodMinMs: 7000, periodMaxMs: 13000 },
    // Bamboo leaves fluttering down on a gentle breeze (green-gold → softer green), density easing.
    gustingWeather(
      { mode: 'fall', color: 0xcfe39a, colorEnd: 0xa9c878,
        size: 6, frequencyMs: 120, lifespanMs: 5400, speed: 12, gravityY: 4, windX: 30, alphaEnd: 0.5 },
      { periodMs: 7800, min: 0.55, max: 1.4, phase: 0.3 },
    ),
    // Faint dappled light shimmer at the grove floor.
    { kind: 'flicker', color: 0xeef3c4, size: 270, baseAlpha: 0.05, flickerAmp: 0.04, hzMin: 2, hzMax: 5, anchors: [{ atX: 0.25, atY: 0.92 }, { atX: 0.55, atY: 0.95 }, { atX: 0.82, atY: 0.92 }] },
    // Slender vertical bamboo stalks (PX) read against the green-gold sky — far/mid/near, LIT green
    // (airy + ordered, NOT dark jungle silhouettes). Far is tallest, lightest + sways (the "whisper"
    // on the breeze); near is shortest, deepest green + most opaque. All 'columns' = vertical stalks.
    ...multiPlaneParallax([
      { svg: WOODLAND_BACKDROPS.bamboo.far, shape: 'columns', color: 0x9ec878, speed: 0.04, height: 380, alpha: 0.64, detail: 1.1, sway: { ampPx: 12, periodMs: 22000 } },
      { svg: WOODLAND_BACKDROPS.bamboo.mid, shape: 'columns', color: 0x77a857, speed: 0.10, height: 280, alpha: 0.80, detail: 1.3, sway: { ampPx: 7, periodMs: 18000 } },
      { svg: WOODLAND_BACKDROPS.bamboo.near, shape: 'columns', color: 0x537f38, speed: 0.18, height: 180, alpha: 0.92, detail: 1.2 },
    ]),
    // Gentle ground mist drifting through the grove.
    { kind: 'fog', color: 0xcde0ad, bands: 4, alphaMin: 0.04, alphaMax: 0.11, driftPx: 46, periodMinMs: 9000, periodMaxMs: 16000 },
    ...biomeCloser({
      // Light, airy vignette — soft green wash, gentle edge (zen, not heavy).
      vignette: { color: 0x24341a, edgeAlpha: 0.2, washColor: 0xbfe086, washAlpha: 0.04 },
      // Warm green-gold grade — a 'normal' veil (tints green-gold WITHOUT the 'screen' lift that would
      // wash the airy biome pale; keeps contrast).
      grade: { top: 0xe6eda6, bottom: 0x82b25e, strength: 0.1, blend: 'normal' },
    }),
  ],
};
